/**
 * creature/templates/fantasy.ts — The Impossible Ones
 *
 * Dragon, griffin, phoenix. These are end-game companions — earned
 * through the highest levels of animal husbandry. They use the
 * winged_quadruped body plan, adding wings to the standard skeleton.
 * Only the most patient and skilled players will ever bond one.
 */

import type { SkeletonDef } from "../skeleton.js";
import type { SpeciesTemplate, GaitPattern } from "../species.js";
import { registerSpecies, createBreed } from "../species.js";

// ─── Dragon Skeleton ────────────────────────────────────────────────────────

const DRAGON_SKELETON: SkeletonDef = {
  id: "skeleton_dragon",
  rootJointId: "hip",
  joints: [
    {
      id: "hip", parentId: null,
      restOffset: { x: 0, y: 0 }, restAngle: -0.3,
      minAngle: -1.0, maxAngle: 0.3, length: 22,
      tags: ["root", "hip"],
    },
    {
      id: "shoulder", parentId: "hip",
      restOffset: { x: 0, y: 0 }, restAngle: -0.1,
      minAngle: -0.4, maxAngle: 0.3, length: 12,
      tags: ["shoulder", "spine"],
    },
    {
      id: "neck", parentId: "shoulder",
      restOffset: { x: 0, y: 0 }, restAngle: -0.7,
      minAngle: -1.5, maxAngle: 0.3, length: 10,
      tags: ["neck"],
    },
    {
      id: "head", parentId: "neck",
      restOffset: { x: 0, y: 0 }, restAngle: 0.3,
      minAngle: -0.5, maxAngle: 0.8, length: 0,
      tags: ["head"],
    },
    // Tail — long and powerful
    {
      id: "tail_0", parentId: "hip",
      restOffset: { x: 8, y: 0 }, restAngle: -0.1,
      minAngle: -1.0, maxAngle: 1.0, length: 10,
      tags: ["tail"],
    },
    {
      id: "tail_1", parentId: "tail_0",
      restOffset: { x: 0, y: 0 }, restAngle: 0.05,
      minAngle: -1.0, maxAngle: 1.0, length: 10,
      tags: ["tail"],
    },
    {
      id: "tail_2", parentId: "tail_1",
      restOffset: { x: 0, y: 0 }, restAngle: 0.1,
      minAngle: -1.5, maxAngle: 1.5, length: 8,
      tags: ["tail"],
    },
    {
      id: "tail_3", parentId: "tail_2",
      restOffset: { x: 0, y: 0 }, restAngle: 0.15,
      minAngle: -2.0, maxAngle: 2.0, length: 6,
      tags: ["tail", "tail_tip"],
    },
    // Wings (from shoulder)
    {
      id: "wing_l_root", parentId: "shoulder",
      restOffset: { x: -3, y: -4 }, restAngle: -1.2,
      minAngle: -2.5, maxAngle: 0.5, length: 16,
      tags: ["wing", "wing_l"],
    },
    {
      id: "wing_l_tip", parentId: "wing_l_root",
      restOffset: { x: 0, y: 0 }, restAngle: -0.3,
      minAngle: -1.5, maxAngle: 1.0, length: 14,
      tags: ["wing", "wing_l", "wing_tip"],
    },
    {
      id: "wing_r_root", parentId: "shoulder",
      restOffset: { x: 3, y: -5 }, restAngle: -1.2,
      minAngle: -2.5, maxAngle: 0.5, length: 16,
      tags: ["wing", "wing_r"],
    },
    {
      id: "wing_r_tip", parentId: "wing_r_root",
      restOffset: { x: 0, y: 0 }, restAngle: -0.3,
      minAngle: -1.5, maxAngle: 1.0, length: 14,
      tags: ["wing", "wing_r", "wing_tip"],
    },
    // Horns
    {
      id: "horn_l", parentId: "head",
      restOffset: { x: -2, y: -3 }, restAngle: -1.8,
      minAngle: -2.0, maxAngle: -1.5, length: 4,
      tags: ["horn", "horn_l"],
    },
    {
      id: "horn_r", parentId: "head",
      restOffset: { x: 2, y: -3.5 }, restAngle: -1.8,
      minAngle: -2.0, maxAngle: -1.5, length: 4,
      tags: ["horn", "horn_r"],
    },
    // Ears (small, membrane-like)
    {
      id: "ear_l", parentId: "head",
      restOffset: { x: -2.5, y: -1.5 }, restAngle: -0.8,
      minAngle: -1.5, maxAngle: 0, length: 3,
      tags: ["ear", "ear_l"],
    },
    {
      id: "ear_r", parentId: "head",
      restOffset: { x: 2, y: -2 }, restAngle: -0.8,
      minAngle: -1.5, maxAngle: 0, length: 3,
      tags: ["ear", "ear_r"],
    },
  ],
  drawOrder: [
    { name: "back_legs",   joints: ["hip"] },
    { name: "tail",        joints: ["hip", "tail_0", "tail_1", "tail_2", "tail_3"] },
    { name: "wings_back",  joints: ["wing_r_root", "wing_r_tip"] },
    { name: "body",        joints: ["hip", "shoulder"] },
    { name: "front_legs",  joints: ["shoulder"] },
    { name: "wings_front", joints: ["wing_l_root", "wing_l_tip"] },
    { name: "head",        joints: ["head", "neck", "ear_l", "ear_r", "horn_l", "horn_r"] },
    { name: "fire_glow",   joints: ["hip", "shoulder"] },
  ],
  ikChains: [
    {
      id: "look_at",
      jointIds: ["shoulder", "neck", "head"],
      targetMode: "point",
      iterations: 3,
      tolerance: 1.0,
    },
  ],
};

// ─── Dragon Gaits ───────────────────────────────────────────────────────────

const DRAGON_WALK: GaitPattern = {
  name: "walk",
  speedRange: [0, 2],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: Math.PI,
    leg_back_l: Math.PI * 0.5, leg_back_r: Math.PI * 1.5,
  },
  strideLength: 0.8,
  bodyBob: 0.25,
  bodyRoll: 0.08,
  headBob: 0.15,
};

const DRAGON_GALLOP: GaitPattern = {
  name: "gallop",
  speedRange: [2, 8],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: 0.3,
    leg_back_l: Math.PI, leg_back_r: Math.PI + 0.3,
  },
  strideLength: 2.0,
  bodyBob: 0.6,
  bodyRoll: 0.05,
  headBob: 0.08,
};

// ─── Dragon Template ────────────────────────────────────────────────────────

export const SPECIES_DRAGON: SpeciesTemplate = {
  id: "dragon",
  displayName: "Dragon",
  bodyPlan: "winged_quadruped",
  skeleton: DRAGON_SKELETON,
  size: "huge",
  baseSpeed: 2.5,
  heightAtShoulder: 2.0,
  gaits: [DRAGON_WALK, DRAGON_GALLOP],
  turnRate: Math.PI * 0.5,
  palette: {
    body: "#2a3828",
    markings: "#1a2818",
    eyes: "#ff6020",
    nose: "#1a1a10",
    accent: "#384830",
    belly: "#404838",
  },
  bodyShape: {
    bodyLength: 22,
    bodyWidth: 7.0,
    headRadius: 6.0,
    tailSegments: 4,
    tailLength: 34,
    earStyle: "pointed",
    earSize: 0.7,
    legThickness: 3.5,
    hasMane: false,
    hasWings: true,
    wingSpan: 30,
    neckLength: 2.5,
    snoutLength: 1.0,
    legLength: 1.6,
  },
  behaviorDefaults: {
    energy: 0.4,
    sociability: 0.15,
    curiosity: 0.6,
    obedience: 0.05,
    playfulness: 0.15,
    courage: 1.0,
    agility: 0.5,
    stamina: 0.95,
    nocturnality: 0.4,
    territoriality: 1.0,
    packInstinct: 0.05,
  },
  rideable: true,
  riderAttachJoint: "shoulder",
  riderOffsetZ: 1.5,
  saddleable: true,
  sounds: {
    idle: ["rumble_deep"],
    happy: ["rumble_warm"],
    hungry: ["growl_low"],
    walk: ["claw_stone"],
    run: ["claw_stone_rapid"],
    special: ["roar", "roar_long", "fire_breath", "growl_deep", "screech"],
  },
  moodContribution: {
    warmth: 0.15,
    coziness: -0.05,
    energy: 0.1,
    nature: 0.12,
  },
};

// ─── Dragon Variants ────────────────────────────────────────────────────────

export const SPECIES_DRAGON_FIRE = createBreed(SPECIES_DRAGON, {
  id: "dragon_fire",
  displayName: "Fire Dragon",
  palette: {
    ...SPECIES_DRAGON.palette,
    body: "#6a1a0a",
    markings: "#4a0a00",
    eyes: "#ffcc00",
    belly: "#8a3a1a",
    accent: "#c06020",
  },
  moodContribution: {
    warmth: 0.25,
    coziness: -0.1,
    energy: 0.15,
    nature: 0.08,
  },
});

export const SPECIES_DRAGON_ICE = createBreed(SPECIES_DRAGON, {
  id: "dragon_ice",
  displayName: "Frost Dragon",
  palette: {
    ...SPECIES_DRAGON.palette,
    body: "#4a6880",
    markings: "#3a5870",
    eyes: "#80d0ff",
    belly: "#6888a0",
    accent: "#5878a0",
  },
  moodContribution: {
    warmth: -0.1,
    coziness: 0.02,
    energy: 0.08,
    nature: 0.15,
  },
});

// ─── Griffin ────────────────────────────────────────────────────────────────

// Griffin shares the dragon skeleton but with different proportions
export const SPECIES_GRIFFIN = createBreed(SPECIES_DRAGON, {
  id: "griffin",
  displayName: "Griffin",
  size: "large",
  baseSpeed: 3.0,
  heightAtShoulder: 1.5,
  riderOffsetZ: 1.0,
  palette: {
    body: "#c8a040",
    markings: "#a08030",
    eyes: "#d0a010",
    nose: "#4a3a20",
    accent: "#e8d080",
    belly: "#d8b860",
  },
  bodyShape: {
    ...SPECIES_DRAGON.bodyShape,
    bodyLength: 18,
    bodyWidth: 5.5,
    headRadius: 4.5,
    tailSegments: 2,
    tailLength: 14,
    wingSpan: 24,
    neckLength: 1.8,
    snoutLength: 0.7,
    legLength: 1.5,
    legThickness: 2.5,
  },
  behaviorDefaults: {
    ...SPECIES_DRAGON.behaviorDefaults,
    sociability: 0.3,
    obedience: 0.2,
    courage: 0.9,
    agility: 0.85,
    territoriality: 0.7,
  },
});

// ─── Phoenix ────────────────────────────────────────────────────────────────

export const SPECIES_PHOENIX = createBreed(SPECIES_DRAGON, {
  id: "phoenix",
  displayName: "Phoenix",
  size: "large",
  baseSpeed: 2.0,
  heightAtShoulder: 1.2,
  riderOffsetZ: 0.8,
  palette: {
    body: "#d04010",
    markings: "#ff6020",
    eyes: "#ffee00",
    nose: "#a03008",
    accent: "#ffa040",
    belly: "#e06830",
  },
  bodyShape: {
    ...SPECIES_DRAGON.bodyShape,
    bodyLength: 14,
    bodyWidth: 4.0,
    headRadius: 3.5,
    tailSegments: 4,
    tailLength: 28,   // long, flowing tail feathers
    wingSpan: 22,
    neckLength: 2.0,
    snoutLength: 0.5,
    legLength: 1.3,
    legThickness: 1.5,
  },
  behaviorDefaults: {
    ...SPECIES_DRAGON.behaviorDefaults,
    sociability: 0.2,
    curiosity: 0.9,
    courage: 0.7,
    energy: 0.5,
    nocturnality: 0.1,
    territoriality: 0.3,
  },
  moodContribution: {
    warmth: 0.2,
    coziness: 0.05,
    energy: 0.08,
    nature: 0.1,
  },
});

// ─── Register ───────────────────────────────────────────────────────────────

export function registerFantasyCreatures(): void {
  registerSpecies(SPECIES_DRAGON);
  registerSpecies(SPECIES_DRAGON_FIRE);
  registerSpecies(SPECIES_DRAGON_ICE);
  registerSpecies(SPECIES_GRIFFIN);
  registerSpecies(SPECIES_PHOENIX);
}
